import { useMemo, useState } from "react";
import { Settings2, CheckCircle2, AlertCircle, Save } from "lucide-react";
import { apiCall, ApiError } from "@/lib/api";
import { SIM_SETTING_DEFS, type SimSettingGroup } from "@shared/sim-settings";

const GROUP_LABEL: Record<SimSettingGroup, string> = {
  attempts: "Attempts & Limits",
  display: "Display & Feedback",
  flow: "Question Flow",
  notifications: "Notifications & Sharing",
  misc: "Misc",
};

const DEFAULTS: Record<string, string> = {
  MaxAttempts: "2",
  TimerEnabled: "No",
  TimerMinutes: "30",
  DefaultMinOpenTextLength: "100",
  ReflectionMinChars: "0",
  ResumeEnabled: "Yes",
  LeaderboardEnabled: "Yes",
  ReflectionScreenRequired: "No",
  ShowScoresDuringSimulation: "No",
  ShowCorrectAnswers: "No",
  AllowBackNavigation: "No",
  RandomizeChoices: "Yes",
  PassingScore: "0",
  CertificateEnabled: "No",
  FeedbackEnabled: "Yes",
  FeedbackTiming: "Immediate",
  ConsequenceTextDisplay: "Always",
  RequireAllNodes: "Yes",
  SaveProgressEnabled: "Yes",
  NotificationsEnabled: "Yes",
  NotifyOnComplete: "Yes",
  NotifyOnFail: "No",
  AnonymousMode: "No",
  PeerComparisonEnabled: "Yes",
  ExportEnabled: "Yes",
  ManagerVisibility: "Yes",
  AdaptiveDifficulty: "No",
  MultiLanguageSupport: "No",
};

interface Props {
  simId: string;
  /** Settings as returned by libraryDetail: existing rows only. */
  initial: Array<{ key: string; value: string }>;
  /** Called after a successful save so the parent can re-pull libraryDetail. */
  onSaved: () => void;
}

/**
 * Edits every simulation_settings key. Catalog (label/type/group/active/hint)
 * is imported from api/shared/sim-settings.ts — the same source the validator
 * reads — so the Live/Coming badges automatically reflect which keys the
 * runtime actually consumes. Save batches all dirty rows into one upsert.
 */
export function SimSettingsEditor({ simId, initial, onSaved }: Props): JSX.Element {
  // initialMap shows the form's CURRENT effective value (explicit row OR
  // spec default). explicitKeys is the set of keys that actually have a DB
  // row — used by dirty-detection so a key whose value matches the spec
  // default but has no DB row is still considered "needs saving". Without
  // this, the first save would skip any key whose toggle/value happened to
  // already equal the spec default — and the runtime endpoint's COALESCE
  // default may differ (e.g. simulations list defaults LeaderboardEnabled
  // to 'No' while the spec is 'Yes'), so the absence of a row silently
  // hides features the author thinks they enabled.
  const initialMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const def of SIM_SETTING_DEFS) {
      m[def.key] = DEFAULTS[def.key] ?? (def.type === "bool" ? "No" : def.type === "number" ? "0" : (def.options?.[0] ?? ""));
    }
    for (const row of initial) {
      m[row.key] = row.value;
    }
    return m;
  }, [initial]);

  const explicitKeys = useMemo(() => new Set(initial.map((r) => r.key)), [initial]);

  const [values, setValues] = useState<Record<string, string>>(initialMap);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dirty = no DB row yet OR value differs from the DB row. First save
  // backfills explicit rows for every setting; subsequent saves only ship
  // genuine edits.
  const dirtyKeys = useMemo(
    () => SIM_SETTING_DEFS.map((d) => d.key).filter((k) => !explicitKeys.has(k) || values[k] !== initialMap[k]),
    [values, initialMap, explicitKeys],
  );

  const handleSave = async (): Promise<void> => {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await apiCall("/librarySettingsUpdate", {
        method: "POST",
        body: {
          simId,
          settings: dirtyKeys.map((k) => ({ key: k, value: values[k] })),
        },
      });
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const groups: SimSettingGroup[] = ["attempts", "display", "flow", "notifications", "misc"];

  return (
    <div className="space-y-5">
      {groups.map((g) => {
        const defs = SIM_SETTING_DEFS.filter((d) => d.group === g);
        if (defs.length === 0) return null;
        return (
          <div key={g} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <h3 className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-200">
              <Settings2 className="h-3.5 w-3.5" /> {GROUP_LABEL[g]}
            </h3>
            <div className="mt-3 grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {defs.map((def) => {
                const v = values[def.key] ?? "";
                const isDirty = v !== initialMap[def.key];
                return (
                  <div
                    key={def.key}
                    className={`flex items-start justify-between gap-3 rounded-lg border ${isDirty ? "border-fuchsia-400/40 bg-fuchsia-500/[0.06]" : "border-white/10 bg-white/[0.02]"} p-3`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-[12px] font-medium text-white/90">{def.label}</p>
                        {def.active ? (
                          <span title="The runtime actually reads this setting today" className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.12em] text-emerald-300">Live</span>
                        ) : (
                          <span title="Stored but the runtime doesn't read it yet — captured for a later release" className="rounded-full border border-white/15 bg-white/[0.04] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.12em] text-white/45">Coming</span>
                        )}
                      </div>
                      {def.hint !== undefined && (
                        <p className="mt-0.5 text-[10.5px] leading-snug text-white/40">{def.hint}</p>
                      )}
                      <p className="mt-0.5 font-mono text-[9.5px] text-white/30">{def.key}</p>
                    </div>
                    <div className="shrink-0">
                      {def.type === "bool" && (
                        <button
                          type="button"
                          onClick={() => setValues((p) => ({ ...p, [def.key]: v === "Yes" ? "No" : "Yes" }))}
                          className={`relative h-6 w-11 rounded-full transition-colors ${v === "Yes" ? "bg-fuchsia-500/60" : "bg-white/10"}`}
                          aria-label={def.label}
                        >
                          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${v === "Yes" ? "left-[1.4rem]" : "left-0.5"}`} />
                        </button>
                      )}
                      {def.type === "number" && (
                        <input
                          type="number"
                          min={def.min}
                          max={def.max}
                          value={v}
                          onChange={(e) => setValues((p) => ({ ...p, [def.key]: e.target.value }))}
                          className="w-20 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-right text-sm text-white outline-none focus:border-fuchsia-400/60"
                        />
                      )}
                      {def.type === "enum" && (
                        <select
                          value={v}
                          onChange={(e) => setValues((p) => ({ ...p, [def.key]: e.target.value }))}
                          className="rounded-md border border-white/15 bg-[#0c0e22] px-2 py-1 text-sm text-white outline-none focus:border-fuchsia-400/60"
                        >
                          {(def.options ?? []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
        <div className="text-[12px] text-white/55">
          {dirtyKeys.length === 0
            ? "All saved."
            : `${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"} pending`}
          {error !== null && (
            <span className="ml-3 inline-flex items-center gap-1 text-rose-300">
              <AlertCircle className="h-3 w-3" /> {error}
            </span>
          )}
          {saved && (
            <span className="ml-3 inline-flex items-center gap-1 text-emerald-300">
              <CheckCircle2 className="h-3 w-3" /> Saved
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={dirtyKeys.length === 0 || saving}
          className="inline-flex items-center gap-2 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-4 py-1.5 text-[12px] font-semibold text-fuchsia-100 transition-colors hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}
